import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'

interface InviteFriendsProps {
  telegramUserId: number
}

interface ReferralStats {
  code: string
  eligible: boolean
  telegramLink: string
  webLink: string
  referralCount: number
  activeReferrals: number
  pendingReferrals: number
  branchCount: number
  branchActiveCount: number
}

export function InviteFriends({ telegramUserId }: InviteFriendsProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [stats, setStats] = useState<ReferralStats | null>(null)
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(false)
  // Shown before an invitation leaves, to someone whose bonus cannot pay out
  // yet. Inviting stays open to everybody — that is what spreads the lottery —
  // but the condition has to reach the sender BEFORE the invitation goes, not
  // after his friend has already won. `pendingSend` holds the action the
  // warning interrupted, so confirming performs that exact action rather than a
  // second copy of it.
  const [pendingSend, setPendingSend] = useState<(() => void) | null>(null)
  const [warningAcknowledged, setWarningAcknowledged] = useState(false)
  const warningRef = useRef<HTMLDivElement | null>(null)

  // The modal body scrolls, and the two triggers sit far apart in it. Without
  // this the panel could appear below the fold and tapping Send or Copy would
  // look like nothing happening at all.
  useEffect(() => {
    if (pendingSend) warningRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [pendingSend])

  const loadReferralData = async () => {
    setLoading(true)
    try {
      const refStats = await api.getReferralStats(telegramUserId)
      setStats(refStats)
    } catch (err) {
      // Keep whatever was last fetched. Blanking it here took the referral code
      // and link off the screen on any transient blip — the user could no
      // longer invite anybody, and there is no retry button in this modal. The
      // one field that could be stale is `eligible`, and the banner below is
      // already guarded on `stats` being present at all.
      console.error('Failed to load referral data:', err)
    }
    setLoading(false)
  }

  const handleOpen = () => {
    setIsOpen(true)
    setPendingSend(null)
    // Reset per opening, not per mount. This component is mounted for the whole
    // session, so acknowledging once would have silenced the warning for every
    // later invitation — the rule is that it reaches the sender before an
    // invitation leaves, not once per page load.
    setWarningAcknowledged(false)
    loadReferralData()
  }

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const performShare = () => {
    if (!stats) return
    const text = '🎟️ Join Hybrid Lottery and win big! Use my referral link:'
    const url = stats.telegramLink

    if (window.Telegram?.WebApp?.openTelegramLink) {
      const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
      window.Telegram.WebApp.openTelegramLink(shareUrl)
    } else {
      handleCopy(url)
    }
  }

  /**
   * Run `action`, unless this user's bonus is inactive and he has not been told.
   *
   * Every way an invitation leaves goes through here — the share sheet and
   * copying the link both put the link in somebody else's hands, and warning on
   * one while the other sits 40px above would buy a guarantee the screen cannot
   * keep.
   *
   * Copying the bare code is deliberately NOT routed through it. A code alone
   * does invite — `/start REF_XXXXXXXX` typed by hand works, and so does a
   * pasted `?ref=` — so this is an accepted gap on a path nobody uses in
   * practice, not a path that is harmless.
   */
  const withWarning = (action: () => void) => () => {
    if (stats && !stats.eligible && !warningAcknowledged) {
      setPendingSend(() => action)
      return
    }
    action()
  }

  const confirmSendAnyway = () => {
    const action = pendingSend
    setWarningAcknowledged(true)
    setPendingSend(null)
    // State set on this tick is not readable yet, so the interrupted action is
    // invoked directly rather than by re-entering the gate.
    action?.()
  }

  return (
    <>
      <button
        onClick={handleOpen}
        className="w-full p-4 rounded-xl flex items-center justify-center gap-3 bg-gradient-to-r from-purple-600/30 to-pink-600/30 border border-purple-500/50"
      >
        <span className="text-2xl">🎁</span>
        <span className="font-semibold text-telegram-text text-sm text-center">
          Invite Friends & Earn 10% of Their Prizes
        </span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="card w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-telegram-text text-lg">Invite Friends</h2>
              <button onClick={() => setIsOpen(false)} className="text-telegram-hint text-2xl">x</button>
            </div>

            <div className="text-center mb-6">
              <div className="text-4xl mb-2">🎁</div>
              <p className="text-telegram-text font-semibold">Earn from your network!</p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="bg-telegram-secondary rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">👤</span>
                  <div>
                    <div className="text-gold-500 font-bold">10% Direct Bonus</div>
                    <div className="text-telegram-hint text-xs">When someone you invited wins — paid from their prize, they keep 90%</div>
                  </div>
                </div>
              </div>
            </div>

            {/* `stats` is null when the load failed, and `!stats?.eligible`
                would then assert that this user has not bought a ticket on the
                strength of a request that never answered. */}
            {stats && !stats.eligible && (
              <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2">
                  <span>⚠️</span>
                  <div className="text-sm">
                    <span className="text-yellow-400 font-semibold">Buy a ticket to activate your bonus</span>
                    {/* States the condition and nothing about timing. The real
                        rule today is "you must have bought before your friend's
                        prize is paid out" — recordObligationPair tops up a
                        referrer only while the winner's row is still PENDING —
                        and on the crypto rail a direct claimPrize() leaves the
                        10% unfunded entirely. Neither "after your purchase" nor
                        "once you have a ticket" is true in every case, so this
                        says neither. */}
                    <div className="text-telegram-hint text-xs">
                      You can share now — your bonus is inactive until then
                    </div>
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-8">
                <div className="text-3xl animate-bounce">⏳</div>
              </div>
            ) : stats ? (
              <div className="space-y-4">
                <div className="bg-telegram-secondary rounded-lg p-4">
                  <div className="text-xs text-telegram-hint mb-1">Your referral code</div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-bold text-gold-500 text-lg">{stats.code}</span>
                    <button
                      onClick={() => handleCopy(stats.code)}
                      className="text-sm text-telegram-link"
                    >
                      {copied ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>
                </div>

                <div className="bg-telegram-secondary rounded-lg p-4">
                  <div className="text-xs text-telegram-hint mb-1">Telegram link</div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-sm text-telegram-text truncate flex-1">
                      {stats.telegramLink}
                    </span>
                    <button
                      onClick={withWarning(() => handleCopy(stats.telegramLink))}
                      className="text-sm text-telegram-link whitespace-nowrap"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                {/* For friends who pay with a crypto wallet on the site: the
                    invite rides in their first payment and ties them to you. */}
                {stats.webLink?.startsWith('https://') && (
                  <div className="bg-telegram-secondary rounded-lg p-4">
                    <div className="text-xs text-telegram-hint mb-1">Website link (crypto wallet)</div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-sm text-telegram-text truncate flex-1">
                        {stats.webLink}
                      </span>
                      <button
                        onClick={withWarning(() => handleCopy(stats.webLink))}
                        className="text-sm text-telegram-link whitespace-nowrap"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-telegram-secondary rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gold-500">{stats.activeReferrals}</div>
                    <div className="text-xs text-telegram-hint">Earning you 10%</div>
                  </div>
                  <div className="bg-telegram-secondary rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-telegram-text">{stats.referralCount}</div>
                    <div className="text-xs text-telegram-hint">Invited in total</div>
                  </div>
                </div>

                {stats.pendingReferrals > 0 && (
                  <div className="text-center text-sm text-telegram-hint">
                    +{stats.pendingReferrals} pending (waiting for ticket purchase)
                  </div>
                )}

                {/* Directly above the button that triggers it. Placed higher
                    up the modal it appeared outside the viewport on a phone —
                    the body scrolls, the send button is last, and tapping it
                    would have looked like nothing happening at all. */}
                {pendingSend && (
                  <div
                    ref={warningRef}
                    className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4"
                  >
                    <div className="text-sm text-yellow-400 font-semibold mb-1">
                      Before you send this
                    </div>
                    <div className="text-telegram-hint text-xs mb-3">
                      You have not bought a ticket yet, so this invitation earns you
                      nothing. Buy one to activate your bonus.
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={confirmSendAnyway}
                        className="flex-1 min-w-[8rem] py-2 rounded-lg bg-telegram-secondary text-sm"
                      >
                        Send anyway
                      </button>
                      <button
                        onClick={() => {
                          setPendingSend(null)
                          setIsOpen(false)
                        }}
                        className="flex-1 min-w-[8rem] py-2 rounded-lg bg-gold-500 text-black text-sm font-semibold"
                      >
                        I'll buy a ticket first
                      </button>
                    </div>
                  </div>
                )}

                <button
                  onClick={withWarning(performShare)}
                  className="btn-gold w-full flex items-center justify-center gap-2"
                >
                  <span>📤</span>
                  <span>Share with Friends</span>
                </button>
              </div>
            ) : (
              <div className="text-center py-4 text-telegram-hint">Failed to load referral data</div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
